import React, { Fragment, Component } from "react";
import { basename } from "path";

export const findOptimalImage = (domElement, photo) => {
  if (typeof window === "undefined") {
    console.log(
      `Can't provide an adapted image size for "${photo}" when rendering on a server.`
    );
    return undefined;
  }

  const requiredMinimalWidth = domElement.clientWidth;
  // ENHANCEMENT
  // --> Detect resize event and recalculate!
  //

  const matched = photo.images.find(
    image => image.width >= requiredMinimalWidth
  );

  if (matched !== undefined) {
    return matched.path;
  }

  return photo.images[photo.images.length - 1].path;
};

export const load = path => require(`../../public/uploads/${basename(path)}`);
export const loadStack = paths => paths.map(load);

// Usage:
// <LoadAdaptedImage path="..." /> will map required properties (src,srcSet,sizes,placeholder) to a single children element.
// or <LoadAdaptedImage photo={require(`../../public/uploads/${basename(nextProps.path)}`)} />

export class LoadAdaptedImage extends Component {
  state = { path: undefined, image: undefined, isMounted: false };

  constructor(props) {
    super(props);

    this.element = React.createRef();
  }

  static getDerivedStateFromProps = ({ photo, path }, prevState) => {
    if (photo !== undefined && prevState.path !== photo.src) {
      return {
        path: photo.src,
        photo: photo
      };
    }

    if (path === undefined || prevState.path === path) {
      return null;
    }

    return {
      path: path,
      photo: load(path)
    };
  };

  componentDidMount = () => {
    this.setState({
      isMounted: true
    });
  };

  render() {
    const children = React.Children.only(this.props.children);
    return (
      <div
        ref={this.element}
        children={React.cloneElement(
          children,
          this.state.isMounted
            ? {
                src: findOptimalImage(this.element.current, this.state.photo),
                srcSet: this.state.photo.srcSet,
                placeholder: this.state.photo.placeholder,
                sizes: this.element.current.clientWidth + "px"
              }
            : {
                placeholder: this.state.photo.placeholder
              }
        )}
      />
    );
  }
}

// Preloads an image and returns a placeholder during the loading process.
// Example:
//   <ProgressiveImage path={path.src} placeholder={path.placeholder}>
//     {({ src, isPlaceholder, ... }) =>
//       isPlaceholder ? <Placeholder src={src} /> : <Background src={src} />
//     }
//   </ProgressiveImage>

export class ProgressiveImage extends Component {
  state = { fetched: [], isClient: false, dimensions: {} };

  componentDidMount() {
    if (typeof window === "undefined") return;

    this.fetchImage();

    this.setState({
      isClient: true
    });
  }

  componentDidUpdate = () => {
    if (!this.state.isClient) return;

    this.fetchImage();
  };

  isFetched = () => this.state.fetched.includes(this.props.src);

  fetchImage = () => {
    if (this.isFetched()) return;

    const image = new Image();
    image.src = this.props.src;
    image.onload = () =>
      !this.isFetched() &&
      this.setState({
        fetched: [...this.state.fetched, this.props.src]
      });
  };

  render() {
    // do not pass children because it would overwrite the existing ones
    const { src, placeholder, children, ...rest } = this.props;
    const isFetched = this.isFetched();
    const additionalProps = isFetched ? rest : [];

    return this.props.children({
      src: isFetched ? this.props.src : this.props.placeholder,
      isPlaceholder: !isFetched,
      dimensions: this.state.dimensions,
      ...additionalProps
    });
  }
}

export const ResponsiveImage = ({ pure, path, photo, children }) => (
  <LoadAdaptedImage path={path} photo={photo}>
    <ProgressiveImage
      children={props => {
        const { dimensions, isPlaceholder, placeholder, ...rest } = props;
        return React.cloneElement(React.Children.only(children), {
          ...rest,
          style: props.isPlaceholder
            ? { objectFit: "initial", width: "100%" }
            : undefined
        });
      }}
    />
  </LoadAdaptedImage>
);



// WEBPACK FOOTER //
// /opt/build/repo/src/containers/ProgressiveHelpers.js